/**
 * gpsCalibration — affine transform between (lat, lng) and normalized
 * sheet coordinates (nx, ny).
 *
 * Operational orientation only. Treats a small region of Earth as a flat
 * tangent plane (good enough for any single jobsite — a plan typically
 * covers <2 km, where the spherical error is well under a meter and far
 * below GPS accuracy on a phone). NOT survey-grade.
 *
 * Solver:
 *   nx = ax * lng + ay * lat + a0
 *   ny = bx * lng + by * lat + b0
 *
 *   2 points → similarity transform: translation + uniform scale +
 *              rotation (the 4 degrees of freedom exactly two point
 *              correspondences can determine), solved as a single
 *              complex-number division. GH-15 (real-plan certification,
 *              2026-09-30) — the previous solver ASSUMED rotation = 0
 *              and derived scale via a raw dot-product projection. When
 *              the anchor pair's geographic displacement happened to be
 *              (near-)perpendicular to its sheet displacement — e.g. the
 *              plan is rotated relative to true north, which is the
 *              common case, not the exception — that projection drove
 *              the scale to (near-)zero, collapsing the WHOLE transform
 *              to a constant map while still reporting `ok: true`. The
 *              complex-division form below recovers scale AND rotation
 *              together and has no such failure mode for any two
 *              distinct, non-coincident points.
 *   3+ points → full affine (translation, scale, rotation, shear).
 *               Overdetermined when ≥3: least-squares (normal
 *               equations), computed in anchor-CENTERED coordinates.
 *               GH-15 — raw geographic lng/lat (e.g. -122.419, 37.775)
 *               squared and summed into the normal-equation matrix lose
 *               enough precision, for anchors that are close together
 *               (the normal case — one jobsite), that Cramer's-rule
 *               division suffers catastrophic cancellation and the
 *               solved coefficients silently drift (observed: a point
 *               expected to invert to (.8,.2) instead returned
 *               (.400045,.399959) — a different real-world location).
 *               Centering each anchor's lng/lat on the anchor-set mean
 *               before building the normal equations keeps every value
 *               fed into the solver at the scale of the SITE (meters to
 *               low kilometers in degree terms), not of the planet, and
 *               eliminates the cancellation.
 *   Both paths — reject (ok:false) rather than silently return a
 *   degenerate/ill-conditioned transform: near-zero anchor separation,
 *   near-singular/near-collinear normal-equation matrix (conditioning
 *   check, not just exact-zero determinant), and a residual check that
 *   the solved transform actually reproduces every anchor within a
 *   tight tolerance.
 *
 * Inverse: same affine inverted analytically.
 *
 * Distance helper (Haversine, meters) for accuracy radius rendering.
 */

const EARTH_RADIUS_M = 6371000;

function _haversineMeters(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

/**
 * Solve a 2x2 linear system. Returns null only on EXACT singularity.
 *
 * R-05 (independent recheck, 2026-10-01) — this used to reject on a
 * fixed absolute `|det| < 1e-12`. `_solve2x2` is shared by callers
 * operating in genuinely different magnitude regimes: `sheetToGps`'s
 * forward-matrix coefficients, and the centered lng/lat-squared sums
 * in `_solveCenteredAffine` (which are legitimately far below 1e-12
 * for any real, small jobsite). A single absolute epsilon here cannot
 * be correct for both. Each caller already performs its OWN
 * scale-aware conditioning check (the spread-determinant gate before
 * `_solveCenteredAffine`, `_isInvertible` before `sheetToGps` is ever
 * reachable) — this primitive only needs to guard the true
 * mathematical edge case of an exact zero determinant.
 */
function _solve2x2(a, b, c, d, e, f) {
  // [a b][x]   [e]
  // [c d][y] = [f]
  const det = a * d - b * c;
  if (det === 0) return null;
  return [(e * d - b * f) / det, (a * f - e * c) / det];
}

/**
 * Solve a 3x3 system that arises from CENTERED least-squares normal
 * equations: [[S_l2,S_lt,S_l],[S_lt,S_t2,S_t],[S_l,S_t,n]] * x = v.
 *
 * GH-15/R-05 (independent recheck, 2026-10-01) — the previous generic
 * Cramer's-rule 3x3 solver rejected this system using a fixed absolute
 * `|det| < 1e-12` epsilon. That threshold is unit-dependent: centered
 * lng/lat terms are O(1e-9) or smaller for any real jobsite (a site
 * only needs to span roughly 1e-4 degrees, ~11m, for S_l2/S_t2 to sit
 * near that floor), while the matrix's own `n` entries are O(1-10).
 * This mixed scale makes the RAW determinant tiny by construction —
 * regardless of whether the anchors are actually well-conditioned —
 * so small-but-legitimate job sites were rejected outright.
 *
 * Because l/t are centered (zero-mean by construction — S_l and S_t
 * are the sums of mean-subtracted values, which cancel to ~0 up to
 * float noise), the system decouples EXACTLY: the slope terms (ax,ay)
 * solve independently from the intercept (a0 is simply the mean of
 * the target column), via the already scale-validated 2x2 "spread"
 * sub-matrix the caller uses for its collinearity gate. No separate
 * absolute epsilon is needed here at all.
 */
function _solveCenteredAffine(S_l2, S_t2, S_lt, S_lTarget, S_tTarget, S_target, n) {
  const slope = _solve2x2(S_l2, S_lt, S_lt, S_t2, S_lTarget, S_tTarget);
  if (!slope) return null;
  return [slope[0], slope[1], S_target / n];
}

/**
 * Build the GPS↔sheet affine from N control points.
 * Returns { ok: true, transform } or { ok: false, reason }.
 *
 * `transform` exposes:
 *   gpsToSheet(lat, lng) → {nx, ny}
 *   sheetToGps(nx, ny)   → {lat, lng}
 *   isReady              → boolean
 */
export function buildCalibration(points) {
  const pts = (points || []).filter(
    (p) => Number.isFinite(p?.nx) && Number.isFinite(p?.ny) &&
           Number.isFinite(p?.lat) && Number.isFinite(p?.lng),
  );
  if (pts.length < 2) {
    return { ok: false, reason: "need at least 2 calibration points" };
  }

  // 2-point fit: similarity transform (translation + uniform scale +
  // rotation) via complex-number division. See file header for why.
  if (pts.length === 2) {
    const [p0, p1] = pts;
    const dLng = p1.lng - p0.lng;
    const dLat = p1.lat - p0.lat;
    const dnx = p1.nx - p0.nx;
    const dny = p1.ny - p0.ny;
    const lenGps2 = dLng * dLng + dLat * dLat;
    if (lenGps2 < 1e-16) {
      return { ok: false, reason: "both calibration points have identical GPS" };
    }
    if (dnx * dnx + dny * dny < 1e-12) {
      return { ok: false, reason: "both calibration points are at the same sheet location" };
    }
    // sheetVec / gpsVec as a complex division: Mr + i*Mi.
    // Mr = scale*cos(theta), Mi = scale*sin(theta).
    const Mr = (dnx * dLng + dny * dLat) / lenGps2;
    const Mi = (dny * dLng - dnx * dLat) / lenGps2;
    const scale = Math.hypot(Mr, Mi);
    if (!Number.isFinite(scale) || scale < 1e-9) {
      return { ok: false, reason: "calibration anchors produce zero scale" };
    }
    // nx = Mr*lng - Mi*lat + a0 ; ny = Mi*lng + Mr*lat + b0
    const ax = Mr, ay = -Mi, bx = Mi, by = Mr;
    const a0 = p0.nx - ax * p0.lng - ay * p0.lat;
    const b0 = p0.ny - bx * p0.lng - by * p0.lat;
    // Invertibility gate — R-05. Algebraically guaranteed here (det =
    // scale^2 whenever scale passed the check above), kept explicit
    // and tested rather than assumed so the two code paths share one
    // provably-checked invariant instead of one checked and one not.
    if (!_isInvertible(ax, ay, bx, by)) {
      return { ok: false, reason: "calibration anchors produce a transform that cannot be inverted" };
    }
    return { ok: true, transform: _makeTransform(ax, ay, a0, bx, by, b0, pts) };
  }

  // 3+ points: full affine, least-squares, in anchor-CENTERED
  // coordinates for numerical stability (see file header).
  const meanL = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
  const meanT = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
  let S_l2 = 0, S_t2 = 0, S_lt = 0, S_l = 0, S_t = 0, n = 0;
  let S_lnx = 0, S_tnx = 0, S_nx = 0;
  let S_lny = 0, S_tny = 0, S_ny = 0;
  for (const p of pts) {
    const l = p.lng - meanL, t = p.lat - meanT;
    n += 1;
    S_l2 += l * l; S_t2 += t * t; S_lt += l * t;
    S_l += l; S_t += t;
    S_lnx += l * p.nx; S_tnx += t * p.nx; S_nx += p.nx;
    S_lny += l * p.ny; S_tny += t * p.ny; S_ny += p.ny;
  }
  // Collinearity/spread gate — GH-15. Whether the anchors are too
  // close to a single line is a GEOMETRIC property of the (centered)
  // points, independent of `n`: compare the spread "area" determinant
  // against the spread "diameter" squared, both built only from the
  // l/t (lng/lat) terms so the n-count row/column (an unrelated unit)
  // never distorts the comparison. This ratio is already scale-aware
  // (both sides share the same lng/lat-squared units), so it correctly
  // accepts small-but-well-shaped sites — see `_solveCenteredAffine`
  // above for why no separate absolute 3x3-determinant epsilon is
  // layered on top of it.
  const spreadDet = S_l2 * S_t2 - S_lt * S_lt;
  const spreadScale = ((S_l2 + S_t2) / 2) ** 2;
  if (spreadScale < 1e-24 || spreadDet < 1e-9 * spreadScale) {
    return { ok: false, reason: "calibration points are collinear or too close together for a stable fit" };
  }
  const ax_sol = _solveCenteredAffine(S_l2, S_t2, S_lt, S_lnx, S_tnx, S_nx, n);
  const ay_sol = _solveCenteredAffine(S_l2, S_t2, S_lt, S_lny, S_tny, S_ny, n);
  if (!ax_sol || !ay_sol) {
    return { ok: false, reason: "calibration points are collinear or too close together for a stable fit" };
  }
  // Solved in centered coords: nx = ax'*(lng-meanL) + ay'*(lat-meanT) + a0'.
  // Fold the centering back into a0/b0 so the public transform still
  // takes raw lng/lat.
  const [axC, ayC, a0C] = ax_sol;
  const [bxC, byC, b0C] = ay_sol;
  const ax = axC, ay = ayC, a0 = a0C - axC * meanL - ayC * meanT;
  const bx = bxC, by = byC, b0 = b0C - bxC * meanL - byC * meanT;

  // Residual gate — GH-15 "ready must depend on validated residuals".
  // For an exact 3-point fit this is ~0 algebraically; for 4+ points
  // (overdetermined) a large residual means the anchors are NOT well
  // explained by a single affine map (bad data entry, wrong anchor
  // swapped, etc.) and should block "ready" rather than silently
  // average over it.
  let maxResidual = 0;
  for (const p of pts) {
    const predNx = ax * p.lng + ay * p.lat + a0;
    const predNy = bx * p.lng + by * p.lat + b0;
    maxResidual = Math.max(maxResidual, Math.hypot(predNx - p.nx, predNy - p.ny));
  }
  if (maxResidual > 0.02) {
    return { ok: false, reason: `anchors do not agree on a single transform (residual ${(maxResidual * 100).toFixed(1)}% of sheet) — re-check anchor placement` };
  }

  // Invertibility gate — R-05 (independent recheck, 2026-10-01). The
  // residual check above only proves gpsToSheet reproduces the
  // anchors; it says nothing about whether the map can be inverted at
  // all. If the SHEET-side anchor placements collapse (e.g. several
  // GPS fixes were all logged against the same clicked point, or
  // against points on a line), the solved forward matrix can be
  // singular even though the GPS-side spread gate above passed —
  // "ready" must not claim a working bidirectional transform when
  // sheetToGps cannot actually recover a location.
  if (!_isInvertible(ax, ay, bx, by)) {
    return { ok: false, reason: "sheet-side anchor placements are too close together to invert — GPS lookup on this sheet would not be reliable" };
  }

  return { ok: true, transform: _makeTransform(ax, ay, a0, bx, by, b0, pts) };
}

/**
 * Whether the forward matrix [[ax,ay],[bx,by]] is well-conditioned
 * enough to invert reliably (R-05). Scale-aware: compares the
 * determinant against the matrix's OWN typical entry magnitude rather
 * than a fixed absolute epsilon, so it works for both a small,
 * tightly-anchored jobsite and a large, loosely-anchored one.
 */
function _isInvertible(ax, ay, bx, by) {
  const det = ax * by - ay * bx;
  const scale = (ax * ax + ay * ay + bx * bx + by * by) / 2;
  if (scale < 1e-300) return false;
  return Math.abs(det) >= 1e-9 * scale;
}

function _makeTransform(ax, ay, a0, bx, by, b0, pts) {
  const gpsToSheet = (lat, lng) => ({
    nx: ax * lng + ay * lat + a0,
    ny: bx * lng + by * lat + b0,
  });
  const sheetToGps = (nx, ny) => {
    // Inverse 2x2: [ax ay; bx by] * [lng;lat] = [nx-a0; ny-b0]
    const inv = _solve2x2(ax, ay, bx, by, nx - a0, ny - b0);
    if (!inv) return null;
    return { lng: inv[0], lat: inv[1] };
  };
  return { gpsToSheet, sheetToGps, isReady: true, _params: { ax, ay, a0, bx, by, b0 }, _pts: pts };
}

/**
 * Convert a GPS accuracy radius (meters) into a normalized-sheet radius.
 * Uses the local linear scale derived from the calibration anchors.
 */
export function metersToSheetRadius(transform, lat, lng, meters) {
  if (!transform || !Number.isFinite(meters)) return 0;
  // Probe with a 1-meter eastward and 1-meter northward step at this lat.
  const dLng = 1 / (EARTH_RADIUS_M * Math.cos((lat * Math.PI) / 180) * (Math.PI / 180));
  const dLat = 1 / (EARTH_RADIUS_M * (Math.PI / 180));
  const here = transform.gpsToSheet(lat, lng);
  const east = transform.gpsToSheet(lat, lng + dLng);
  const north = transform.gpsToSheet(lat + dLat, lng);
  const sx = Math.hypot(east.nx - here.nx, east.ny - here.ny);
  const sy = Math.hypot(north.nx - here.nx, north.ny - here.ny);
  const sheetMeter = (sx + sy) / 2;
  return Math.max(0, sheetMeter * meters);
}

export { _haversineMeters as haversineMeters };
